// MACHINERY V2 (Stroje) — Prehľad: the gateway-event FETCH (M2.16d).
//
// One request, one machine, one component-local result. The pure mapping lives
// in prehladEvents.ts; this file owns only "what is asked of the server, when it
// is worth asking, and which machine the answer belongs to".
//
// >>> `[]` IS NOT A FAILURE, AND THAT IS A SERVER GUARANTEE, NOT AN ASSUMPTION.
// <<< CustomerMachineStatusController.GetEventsAsync returns an EMPTY ARRAY for
// a cross-tenant or nonexistent machine (:131-135), deliberately identical to a
// machine that simply has no events — no 403, no 404, no existence disclosure.
// So `rows: []` with `failed: false` is the normal, expected outcome for the
// ~99% of the fleet that has no gateway at all, and the panel must render its
// empty state, never a retry notice. `failed` is set ONLY when the promise
// REJECTS (network, 5xx, an auth failure that does reach the client).
//
// FAIL CLOSED ON REJECTION (the fleetTelemetry.ts:44-47 posture, restated): an
// error yields NO events rather than stale ones, and the caller shows an SR-only
// list plus a retry. Never fail open.
//
// THE REQUEST IS TRACKED PER ID WITH A MONOTONIC TOKEN — the MachineProfileV2
// .tsx:110-128 pattern (requestRef + the two token tests), not
// tabs/useMachineScope.ts. useMachineScope exists for
// surfaces that read an APP-WIDE Redux slice nothing clears; these events live
// in this hook's own state, so ownership is expressible directly and a stale
// fulfilment is simply dropped by the token. Navigating machine A -> B fast
// enough that A resolves after B therefore cannot paint A's events under B.
//
// NO POLL. The fleet telemetry poller exists because a STATUS badge that freezes
// is actively misleading under a header that says LIVE; an event LOG that is one
// navigation old is not. When the deferred SignalR push lands (ticket §6/§7) this
// becomes a subscription — add the push, not an interval.
//
// ============================================================
//  >>> THE REQUEST IS GATED ON `enabled`, AND THAT IS NOT AN OPTIMISATION
//  >>> DRESSED UP AS A FEATURE (M2.16d review, item 5).
// ============================================================
// The caller passes `presentation.showStatusRow`, the M2.1 contract's own "this
// machine has a gateway connection" flag (fleet/cardStates.ts:276 —
// `state !== "unconnected"`), which is the SAME flag PrehladLiveCard already
// gates its KPI cells on. An unconnected machine is one the gateway's own
// machine list does not carry, i.e. one with no connection through which an
// event could ever have been written, so the answer is `[]` by construction —
// and `[]` is exactly what this hook reports when the gate is closed. The
// RENDERED RESULT IS IDENTICAL; what disappears is a guaranteed-empty
// cross-service round trip on ~99% of profile opens.
//
// `enabled` ARRIVES FALSE AND MAY FLIP TRUE — the fleet poller starts with an
// empty map, so EVERY machine reads `unconnected` on the first frame and a
// connected one only becomes enabled when the first poll resolves. Two
// consequences are designed for rather than discovered:
//   · `settled` (below) does NOT go back to false on that flip. A machine that
//     has already shown its list must not collapse to a skeleton a second later
//     because telemetry arrived; the events then ARRIVE INTO the list, which is
//     an append, not a correction.
//   · `loading` DOES go true for the flip, because it means "a request is in
//     flight" and drives the retry button's busy state. The two flags are
//     separate precisely because they answer different questions.
import { useCallback, useEffect, useRef, useState } from "react";

import api from "../../../../../core/api";
import { getGatewayRequestConfig } from "../../fleet/fleetTelemetry";
import { PrehladEventDto } from "./prehladEvents";

/** How many rows to ask for. The server default is 50 and it clamps to 1..500
 *  (MachineEventsRepository.GetRecentAsync:25); the card shows at most
 *  PREHLAD_ROW_LIMIT after the SR merge, and asking for a few more leaves room
 *  for the merge to interleave without a second page. */
export const PREHLAD_EVENT_FETCH_LIMIT = 20;

/** ONE array for every "no events" answer. A fresh `[]` per gate-closed effect
 *  run is a new reference, which would re-render the tab for nothing. */
const NO_EVENTS: PrehladEventDto[] = [];

export interface PrehladEventsFeed {
  /** Newest-first, as the server ordered them. Empty is a NORMAL state. */
  events: PrehladEventDto[];
  /** A request for THIS machine is in flight. Drives the retry button's busy
   *  state — NOT the panel's skeleton (that is `settled`). */
  loading: boolean;
  /**
   * This machine's events side has produced an answer at least once — a
   * response, a rejection, or "the gate is closed so the answer is `[]` by
   * construction". FALSE only before the first of those, which is the one
   * window in which the panel may not yet speak about the machine.
   */
  settled: boolean;
  /** True only when THIS machine's request REJECTED. Never true for `[]`.
   *  Stays true across a retry until that retry itself settles. */
  failed: boolean;
  refetch: () => void;
}

export const usePrehladEvents = (
  machineId: string,
  enabled: boolean
): PrehladEventsFeed => {
  const [events, setEvents] = useState<PrehladEventDto[]>(NO_EVENTS);
  // Seeded from the arguments rather than from a constant: an unconnected
  // machine is settled on its FIRST FRAME, so the panel never paints a skeleton
  // it is going to remove again after the effect runs (useEffect fires after
  // paint, so a `useState(false)` here would flash on ~99% of the fleet).
  const [settled, setSettled] = useState<boolean>(() => !machineId || !enabled);
  const [loading, setLoading] = useState<boolean>(() => !!machineId && enabled);
  const [failed, setFailed] = useState(false);
  /** Monotonic request token — only the NEWEST fulfilment may write state. */
  const requestRef = useRef(0);

  const run = useCallback((target: string) => {
    const token = ++requestRef.current;
    setLoading(true);
    // `failed` is deliberately NOT cleared here (M2.16d review, item 4). It used
    // to be, which unmounted the RetryNotice the instant Retry was pressed — so
    // the busy state that notice renders could never appear, and a failing
    // endpoint could be hammered. It clears on SUCCESS, which is the same rule
    // tabs/useMachineScope.ts applies to its own `failedFor`.
    api
      .get<PrehladEventDto[] | null>(`/v1/gateway/machines/${target}/events`, {
        ...getGatewayRequestConfig(),
        params: { limit: PREHLAD_EVENT_FETCH_LIMIT },
      })
      .then((res) => {
        if (requestRef.current !== token) return;
        // A 200 with a null body is the same normal empty state — see the
        // header on why empty is never an error.
        setEvents(Array.isArray(res.data) && res.data.length > 0 ? res.data : NO_EVENTS);
        setFailed(false);
        setLoading(false);
        setSettled(true);
      })
      .catch(() => {
        if (requestRef.current !== token) return;
        setEvents(NO_EVENTS);
        setFailed(true);
        setLoading(false);
        setSettled(true);
      });
  }, []);

  useEffect(() => {
    if (!machineId || !enabled) {
      // Any in-flight fulfilment loses its right to write state: the gate
      // closing is an answer, and a late response must not overwrite it.
      requestRef.current += 1;
      setEvents(NO_EVENTS);
      setLoading(false);
      setFailed(false);
      setSettled(true);
      return;
    }
    // A MACHINE CHANGE clears the previous machine's failure; a RETRY does not
    // (see `run`). That split is the whole of item 4.
    setFailed(false);
    run(machineId);
  }, [machineId, enabled, run]);

  const refetch = useCallback(() => {
    if (machineId && enabled) run(machineId);
  }, [machineId, enabled, run]);

  return { events, loading, settled, failed, refetch };
};

export default usePrehladEvents;
